import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import type { TranslationKey } from '@devils-paw/i18n';

export type AudioCue = 'seal' | 'tick' | 'flip' | 'win';

interface AudioValue {
  muted: boolean;
  setMuted: (muted: boolean) => void;
  play: (cue: AudioCue) => void;
}

const AudioContextState = createContext<AudioValue | null>(null);

const CAPTION_KEYS: Record<AudioCue, TranslationKey> = {
  seal: 'audio.seal', tick: 'audio.tick', flip: 'audio.flip', win: 'audio.win',
};

function synthesize(context: AudioContext, cue: AudioCue): void {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const now = context.currentTime;
  const settings = cue === 'seal'
    ? { frequency: 95, end: 0.13, volume: 0.22, type: 'square' as OscillatorType }
    : cue === 'tick'
      ? { frequency: 720, end: 0.055, volume: 0.06, type: 'sine' as OscillatorType }
      : cue === 'flip'
        ? { frequency: 260, end: 0.09, volume: 0.08, type: 'triangle' as OscillatorType }
        : { frequency: 150, end: 0.3, volume: 0.18, type: 'sawtooth' as OscillatorType };
  oscillator.type = settings.type;
  oscillator.frequency.setValueAtTime(settings.frequency, now);
  oscillator.frequency.exponentialRampToValueAtTime(settings.frequency * (cue === 'win' ? 2.4 : 0.7), now + settings.end);
  gain.gain.setValueAtTime(settings.volume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + settings.end);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start(now);
  oscillator.stop(now + settings.end);
}

export function AudioProvider({ children, text }: { children: ReactNode; text: (key: TranslationKey) => string }) {
  const [muted, setMutedState] = useState(() => localStorage.getItem('devils-paw:muted') === 'true');
  const [caption, setCaption] = useState('');
  const contextRef = useRef<AudioContext | null>(null);

  const setMuted = useCallback((next: boolean) => {
    localStorage.setItem('devils-paw:muted', String(next));
    setMutedState(next);
  }, []);

  const play = useCallback((cue: AudioCue) => {
    setCaption('');
    window.setTimeout(() => setCaption(text(CAPTION_KEYS[cue])), 0);
    if (muted) return;
    const AudioContextConstructor = window.AudioContext ?? window.webkitAudioContext;
    if (AudioContextConstructor === undefined) return;
    contextRef.current ??= new AudioContextConstructor();
    if (contextRef.current.state === 'suspended') void contextRef.current.resume();
    synthesize(contextRef.current, cue);
  }, [muted, text]);

  const value = useMemo(() => ({ muted, setMuted, play }), [muted, play, setMuted]);
  return (
    <AudioContextState.Provider value={value}>
      {children}
      <p className="sr-only" aria-live="polite" aria-atomic="true">{caption}</p>
    </AudioContextState.Provider>
  );
}

export function useAudio(): AudioValue {
  const value = useContext(AudioContextState);
  if (value === null) throw new Error('useAudio must be used inside AudioProvider');
  return value;
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}
